<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { $api } from '@/services';
import { _ROUTER_NAME } from '@/enums';
import Loading from '@/components/common/Loading.vue';
import DaIcon from '@/components/elements/daIcon/DaIcon.vue';
import Checkbox from '@/components/elements/checkbox/Checkbox.vue';
import { toEnglish } from '@/helper/toEnglish';
import {
	readPackageLock,
	writePackageLock,
} from '@/views/packages/packageLockStorage';
import checkRing from '@/assets/images/packages/lock-check-ring.svg';
import checkMark from '@/assets/images/packages/lock-check-mark.svg';

const route = useRoute();
const router = useRouter();
const store = useStore();

const isLoading = ref(true);
const inProgress = ref(false);
const query = ref('');
const searchText = ref('');
const students = ref([]);
const selectedIds = ref([]);
const confirmOpen = ref(false);
const contractId = ref(null);
const apiRequired = ref(0);
let searchTimer = null;
let loadSeq = 0;

const idKey = (id) => String(id);

const requiredCount = computed(() => {
	const fromQuery = Number(route.query.required);
	if (fromQuery > 0) return fromQuery;
	if (apiRequired.value > 0) return apiRequired.value;
	const draft = readPackageLock();
	const seats = Number(draft?.defaultSeats) || 0;
	const chosen =
		Number(draft?.totalSeats) || Number(draft?.studentCount) || 0;
	return Math.max(0, seats - chosen);
});

const selectedLookup = computed(() => {
	const lookup = {};
	selectedIds.value.forEach((id) => {
		lookup[idKey(id)] = true;
	});
	return lookup;
});

const selectedStudents = computed(() =>
	students.value.filter((student) => selectedLookup.value[idKey(student.id)])
);

const filteredStudents = computed(() => {
	const text = searchText.value.trim().toLowerCase();
	if (!text) return students.value;
	return students.value.filter((student) => {
		const name = String(student.name || '');
		const folded = toEnglish(name).toLowerCase();
		return folded.includes(text) || name.toLowerCase().includes(text);
	});
});

const currentStudents = computed(() => {
	const draft = readPackageLock();
	return Number(draft?.defaultSeats) || students.value.length;
});

const newSeats = computed(() => {
	const draft = readPackageLock();
	const saved = Number(draft?.totalSeats) || Number(draft?.studentCount);
	if (saved > 0) return saved;
	return Math.max(0, currentStudents.value - requiredCount.value);
});

const canConfirm = computed(
	() =>
		requiredCount.value > 0 &&
		selectedIds.value.length === requiredCount.value
);

const centerName = (student) =>
	student.center?.name ||
	student.center_name ||
	student.org_name ||
	student.org?.name ||
	'-';

const unwrapPayload = (body) => {
	if (body && Object.prototype.hasOwnProperty.call(body, 'data')) {
		return body.data;
	}
	return body;
};

const readContractId = (meta) =>
	meta?.contract_id || meta?.contract?.id || null;

const readRequired = (meta) => {
	const value = Number(
		meta?.students_to_close_count ??
			meta?.required_count ??
			meta?.seats_to_close ??
			meta?.to_close_count
	);
	return value > 0 ? value : 0;
};

const loadStudents = async () => {
	isLoading.value = true;
	const requestId = ++loadSeq;
	try {
		const res = await $api.leader.listStudentsToClose();
		if (requestId !== loadSeq) return;
		const payload = unwrapPayload(res?.data);
		const list = Array.isArray(payload)
			? payload
			: Array.isArray(payload?.students)
			? payload.students
			: Array.isArray(payload?.data)
			? payload.data
			: [];
		students.value = list;
		const meta = payload && !Array.isArray(payload) ? payload : {};
		const fromApi = readContractId(meta);
		const queryId = route.query.contractId || route.query.contract_id;
		const fromRoute = Array.isArray(queryId) ? queryId[0] : queryId;
		const fromDraft = readPackageLock()?.contractId;
		contractId.value = fromRoute || fromApi || fromDraft || null;
		apiRequired.value = readRequired(meta);

		const allowed = {};
		list.forEach((student) => {
			allowed[idKey(student.id)] = true;
		});
		const draft = readPackageLock();
		const saved = Array.isArray(draft?.students)
			? draft.students.map((item) => item.id)
			: [];
		const marked = Array.isArray(meta.student_ids_to_close)
			? meta.student_ids_to_close
			: list
					.filter((student) => student.selected || student.to_close)
					.map((student) => student.id);
		const preferred = saved.length ? saved : marked;
		const limit = requiredCount.value;
		selectedIds.value = preferred
			.filter((id) => allowed[idKey(id)])
			.slice(0, limit > 0 ? limit : preferred.length);
	} catch (err) {
		if (requestId !== loadSeq) return;
		students.value = [];
		console.error(err);
	} finally {
		if (requestId === loadSeq) isLoading.value = false;
	}
};

const isChecked = (id) => !!selectedLookup.value[idKey(id)];

const atLimit = (id) =>
	!isChecked(id) && selectedIds.value.length >= requiredCount.value;

const toggle = (id) => {
	if (isChecked(id)) {
		selectedIds.value = selectedIds.value.filter(
			(item) => idKey(item) !== idKey(id)
		);
		return;
	}
	if (selectedIds.value.length >= requiredCount.value) return;
	selectedIds.value = [...selectedIds.value, id];
};

const onSearch = () => {
	clearTimeout(searchTimer);
	searchTimer = setTimeout(() => {
		searchText.value = query.value;
	}, 300);
};

const goBack = () => {
	router.push({ name: _ROUTER_NAME.PACKAGE_BUYING_PACKAGE });
};

const confirmLock = async () => {
	if (!canConfirm.value || inProgress.value) return;
	inProgress.value = true;
	try {
		if (contractId.value) {
			const res = await $api.leader.closeContractStudents(
				contractId.value,
				selectedIds.value
			);
			if (!res?.data) return;
		}
		const draft = readPackageLock() || {};
		writePackageLock({
			...draft,
			contractId: contractId.value,
			students: selectedStudents.value.map((student) => ({
				id: student.id,
				name: student.name,
			})),
		});
		confirmOpen.value = false;
		router.push({ name: _ROUTER_NAME.PACKAGE_BUYING_PACKAGE });
	} catch (err) {
		const errors = err?.response?.data?.errors;
		store.dispatch('setAlertMessage', {
			message:
				Array.isArray(errors) && errors.length
					? errors[0]
					: 'Không thể lưu danh sách học sinh cần khoá.',
			type: 'danger',
		});
	} finally {
		inProgress.value = false;
	}
};

onMounted(loadStudents);
onUnmounted(() => clearTimeout(searchTimer));
</script>

<template>
	<div v-if="isLoading" class="fixed top-1/2 left-1/2">
		<Loading />
	</div>
	<div v-else class="grid grid-cols-2 min-h-screen md-down:grid-cols-1">
		<div class="__setup-center px-12 md-down:px-4 pb-30">
			<div
				class="__setup-center__header pt-10 pb-4 flex justify-between gap-8 items-center lg:flex-nowrap flex-wrap"
			>
				<div
					class="__record__title text-neutral-500 font-semibold whitespace-nowrap flex flex-nowrap items-center text-4xl cursor-pointer"
					@click="goBack"
				>
					<svg
						class="mr-2"
						width="28"
						height="28"
						viewBox="0 0 28 28"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M17.5 7L10.5 14L17.5 21"
							stroke="#94A3B8"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
					Khoá học sinh
				</div>
				<div
					class="search-input w-80 lg-down:ml-auto"
					style="min-width: 120px"
				>
					<div class="input-group input--xl">
						<input
							v-model="query"
							type="text"
							placeholder="Tìm theo học sinh"
							class="bg-white input-with-icon"
							@keyup="onSearch"
						/>
						<div class="input-icon absolute top-1/2">
							<DaIcon
								name="das--search"
								class="h-4 w-4 text-neutral-400"
							/>
						</div>
					</div>
				</div>
			</div>

			<div class="mt-6">
				<div v-if="filteredStudents.length">
					<div class="font-semibold text-neutral-600">
						Chọn học sinh để khoá khi kích hoạt gói mới
					</div>
					<div class="shadow-sm sm:rounded-2xl mt-4">
						<div class="__record__table overflow-x-auto scroll-bar">
							<div
								class="overflow-hidden sm:rounded-t-2xl w-fit min-w-full"
							>
								<table
									class="min-w-full divide-y text-neutral-200"
								>
									<thead class="bg-neutral-100 h-16">
										<tr>
											<th
												scope="col"
												class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider w-16 whitespace-nowrap"
											>
												STT
											</th>
											<th
												scope="col"
												class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
											>
												Tên học sinh
											</th>
											<th
												scope="col"
												class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
											>
												Cơ sở
											</th>
											<th class="w-8"></th>
										</tr>
									</thead>
									<tbody>
										<tr
											v-for="(student, index) in filteredStudents"
											:key="student.id"
											:class="
												index % 2 === 0
													? 'bg-white'
													: 'bg-neutral-50'
											"
											class="text-neutral-700"
										>
											<td>
												<div class="px-6 py-4 text-neutral-700">
													{{ index + 1 }}
												</div>
											</td>
											<td
												class="px-6 py-4 whitespace-nowrap text-neutral-700"
											>
												<div class="flex items-center">
													<img
														v-if="student.profile_photo?.w200"
														class="shadow-sm inline-block h-6 w-6 rounded-full"
														:src="student.profile_photo.w200"
														alt=""
													/>
													<span
														v-else
														class="inline-flex items-center justify-center h-6 w-6 rounded-full"
														:style="{
															background:
																student.profile_photo?.default
																	?.color || '#F1A267',
														}"
													>
														<span
															class="font-medium leading-none text-white text-xs"
														>
															{{
																student.profile_photo?.default
																	?.initial
															}}
														</span>
													</span>
													<div
														class="ml-4 font-semibold truncate"
														:style="{
															color:
																student.profile_photo?.default
																	?.color || '#F1A267',
														}"
													>
														{{ student.name }}
													</div>
												</div>
											</td>
											<td class="px-6 py-4 whitespace-nowrap">
												{{ centerName(student) }}
											</td>
											<td>
												<Checkbox
													:value="isChecked(student.id)"
													:disabled="atLimit(student.id)"
													@click.stop="toggle(student.id)"
												/>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
				<div
					v-else
					class="w-full h-full mt-20 flex items-center justify-center flex-col"
				>
					<img
						src="@/assets/images/illustrations/not_found.png"
						alt=""
						height="250"
						width="250"
					/>
					<div
						class="text-neutral-500 text-lg text-center"
						style="max-width: 700px"
					>
						Rất tiếc, chúng tôi không thể tìm thấy kết quả cho tìm
						kiếm của bạn.
					</div>
				</div>
			</div>
		</div>

		<div class="bg-white p-12 md-down:px-4">
			<div class="font-semibold text-neutral-700 text-xl">
				Danh sách học sinh đã chọn
			</div>
			<div class="text-neutral-400 mt-1 text-lg">
				Gói mới có {{ newSeats }} suất học sinh, trong khi trung tâm hiện
				có {{ currentStudents }} học sinh đang hoạt động. Vui lòng chọn
				{{ requiredCount }} học sinh sẽ bị khóa khi gói mới được kích
				hoạt.
			</div>
			<div class="flex items-center gap-5 mt-6">
				<div
					class="flex items-center justify-between gap-2 py-4 px-6 shadow-sm w-full rounded-xl"
				>
					<div class="font-normal" style="color: #65758b; font-size: 14px">
						Số học sinh đã chọn
					</div>
					<div class="text-4xl font-bold" style="color: #48566a">
						{{ selectedIds.length }}
					</div>
				</div>
				<div
					class="flex items-center justify-between gap-2 py-4 px-6 shadow-sm w-full rounded-xl"
				>
					<div class="font-normal" style="color: #65758b; font-size: 14px">
						Số học sinh cần khoá
					</div>
					<div class="text-4xl font-bold" style="color: #48566a">
						{{ requiredCount }}
					</div>
				</div>
			</div>

			<div
				v-if="selectedStudents.length"
				class="shadow-sm sm:rounded-2xl mt-6"
			>
				<div class="__record__table overflow-x-auto scroll-bar">
					<div class="overflow-hidden sm:rounded-t-2xl w-fit min-w-full">
						<table class="min-w-full divide-y text-neutral-200">
							<thead class="bg-neutral-100 h-16">
								<tr>
									<th
										scope="col"
										class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider w-16 whitespace-nowrap"
									>
										STT
									</th>
									<th
										scope="col"
										class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
									>
										Tên học sinh
									</th>
									<th
										scope="col"
										class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
									>
										Cơ sở
									</th>
									<th class="w-8"></th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="(student, index) in selectedStudents"
									:key="student.id"
									:class="
										index % 2 === 0 ? 'bg-white' : 'bg-neutral-50'
									"
									class="text-neutral-700"
								>
									<td>
										<div class="px-6 py-4 text-neutral-700">
											{{ index + 1 }}
										</div>
									</td>
									<td class="px-6 py-4 whitespace-nowrap text-neutral-700">
										<div class="flex items-center">
											<img
												v-if="student.profile_photo?.w200"
												class="shadow-sm inline-block h-6 w-6 rounded-full"
												:src="student.profile_photo.w200"
												alt=""
											/>
											<span
												v-else
												class="inline-flex items-center justify-center h-6 w-6 rounded-full"
												:style="{
													background:
														student.profile_photo?.default?.color ||
														'#F1A267',
												}"
											>
												<span
													class="font-medium leading-none text-white text-xs"
												>
													{{
														student.profile_photo?.default?.initial
													}}
												</span>
											</span>
											<div
												class="ml-4 font-semibold whitespace-pre-wrap w-40"
												:style="{
													color:
														student.profile_photo?.default?.color ||
														'#F1A267',
												}"
											>
												{{ student.name }}
											</div>
										</div>
									</td>
									<td class="px-6 py-4 whitespace-nowrap">
										{{ centerName(student) }}
									</td>
									<td>
										<Checkbox
											:value="true"
											@click.stop="toggle(student.id)"
										/>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<div class="mt-6 flex justify-end">
				<button
					type="button"
					class="button-p text-center"
					style="width: 300px"
					:disabled="!canConfirm || inProgress"
					@click="confirmOpen = true"
				>
					Xác nhận
				</button>
			</div>
		</div>
	</div>

	<div
		v-if="confirmOpen"
		class="lock-modal"
		@click.self="confirmOpen = false"
	>
		<div class="lock-modal__card">
			<div class="lock-modal__body">
				<div class="lock-modal__icon">
					<img :src="checkRing" alt="" width="60" height="60" />
					<img
						class="lock-modal__mark"
						:src="checkMark"
						alt=""
						width="46"
						height="46"
					/>
				</div>
				<h2>Xác nhận học sinh sẽ được khóa</h2>
				<p>
					Bạn đã chọn <b>{{ selectedIds.length }} học sinh</b> sẽ bị khóa
					khi gói mới được kích hoạt.
				</p>
				<p>
					Các học sinh này
					<b>vẫn hoạt động bình thường trong gói hiện tại</b>
					cho đến khi gói hiện tại kết thúc.
				</p>
				<p>
					Khi gói mới bắt đầu, hệ thống sẽ tự động khóa
					{{ selectedIds.length }} học sinh đã chọn và kích hoạt gói mới
					cho các học sinh còn lại
				</p>
				<p>
					Bạn có thể thay đổi danh sách học sinh trước khi hoàn tất đăng
					ký.
				</p>
			</div>
			<div class="lock-modal__footer">
				<button
					type="button"
					:disabled="inProgress"
					@click="confirmOpen = false"
				>
					Quay lại
				</button>
				<button
					type="button"
					class="button-p"
					:disabled="inProgress"
					@click="confirmLock"
				>
					Xác nhận
				</button>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.lock-modal {
	position: fixed;
	inset: 0;
	z-index: 50;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 16px;
	background: rgba(107, 114, 128, 0.75);
}

.lock-modal__card {
	width: 600px;
	max-width: 100%;
	overflow: hidden;
	background: #fff;
	border-radius: 16px;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.1), 0 20px 24px rgba(20, 37, 63, 0.06);
}

.lock-modal__body {
	padding: 24px 48px;
	text-align: center;

	h2 {
		margin: 20px 0 8px;
		color: #344256;
		font-size: 20px;
		line-height: 28px;
		font-weight: 600;
	}

	p {
		color: #65758b;
		font-size: 16px;
		line-height: 24px;
	}

	p + p {
		margin-top: 16px;
	}

	b {
		color: #344256;
		font-weight: 600;
	}
}

.lock-modal__icon {
	position: relative;
	width: 60px;
	height: 60px;
	margin: 0 auto;

	img {
		display: block;
	}
}

.lock-modal__mark {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%) rotate(180deg) scaleX(-1);
}

.lock-modal__footer {
	display: flex;
	gap: 24px;
	padding: 24px 48px;
	background: #f8fafc;

	button {
		flex: 1;
		padding: 12px 18px;
		border: 0;
		background: transparent;
		color: #65758b;
		font-size: 14px;
		line-height: 20px;
		font-weight: 600;
		cursor: pointer;
	}

	.button-p {
		background: #f0bb4f;
		color: #344256;
		border-radius: 4px;
	}

	button:disabled {
		opacity: 0.6;
		cursor: default;
	}
}
</style>
